import { useEffect, useRef } from "react";

/**
 * useSupportTicketLive — subscribes to a support ticket's live thread.
 *
 * Guarantees:
 *   • WebSocket reconnects automatically with exponential backoff (1s → 30s cap).
 *   • On every (re)connect, the caller's `onSync()` runs so any messages
 *     missed while disconnected are re-hydrated from the server.
 *   • Keepalive ping every 25s so proxies/load-balancers don't kill the socket.
 *   • Polling fallback runs a lightweight refresh every 2s whenever the WS
 *     isn't connected — restrictive networks (corporate proxies, some carriers)
 *     that block WS still see near-real-time updates.
 *
 * The caller owns the actual state. This hook just fires callbacks:
 *   onMessage(msg)      new thread message pushed by the server
 *   onPresence(payload) user/staff online status
 *   onTyping(payload)   { who: 'user'|'staff', is_typing }
 *   onClaim(payload)    { claimed_by }
 *   onStateChange(s)    'connecting'|'connected'|'disconnected'|'error'
 *   onSync()            called after every connect + on polling ticks so the
 *                       caller re-fetches the full thread
 *
 * Params:
 *   enabled    — turn the whole thing off (e.g. no ticket selected)
 *   wsUrl      — full WSS URL including auth (token or jwt query param)
 *   pollMs     — fallback polling cadence in ms (default 2000)
 */
export default function useSupportTicketLive({
  enabled,
  wsUrl,
  onMessage,
  onPresence,
  onTyping,
  onClaim,
  onStateChange,
  onSync,
  pollMs = 5000,
}) {
  const wsRef = useRef(null);
  const backoffRef = useRef(1000);
  const reconnectTimer = useRef(null);
  const pingTimer = useRef(null);
  const pollTimer = useRef(null);
  const cancelledRef = useRef(false);
  const connectedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !wsUrl) return () => {};
    cancelledRef.current = false;

    const setState = (s) => { onStateChange?.(s); connectedRef.current = s === "connected"; };

    const clearTimers = () => {
      if (reconnectTimer.current) { clearTimeout(reconnectTimer.current); reconnectTimer.current = null; }
      if (pingTimer.current) { clearInterval(pingTimer.current); pingTimer.current = null; }
    };

    const scheduleReconnect = () => {
      if (cancelledRef.current) return;
      clearTimers();
      const delay = Math.min(backoffRef.current, 30000);
      reconnectTimer.current = setTimeout(() => { backoffRef.current *= 2; connect(); }, delay);
    };

    const connect = () => {
      if (cancelledRef.current) return;
      setState("connecting");
      let ws;
      try { ws = new WebSocket(wsUrl); }
      catch { scheduleReconnect(); return; }
      wsRef.current = ws;

      ws.onopen = () => {
        backoffRef.current = 1000;
        setState("connected");
        // Re-sync the thread on every reconnect — the server won't replay
        // messages that arrived while we were offline.
        try { onSync?.(); } catch { /* caller may be unmounting */ }
        // Keepalive so idle proxies don't kill the socket.
        pingTimer.current = setInterval(() => {
          if (ws.readyState === 1) {
            try { ws.send(JSON.stringify({ type: "ping" })); } catch { /* socket busy */ }
          }
        }, 25000);
      };

      ws.onmessage = (ev) => {
        let msg; try { msg = JSON.parse(ev.data); } catch { return; }
        if (msg.type === "message") onMessage?.(msg.message);
        else if (msg.type === "presence") onPresence?.(msg);
        else if (msg.type === "typing")  onTyping?.(msg);
        else if (msg.type === "claim")   onClaim?.(msg);
      };

      ws.onerror = () => setState("error");
      ws.onclose = () => {
        setState("disconnected");
        clearTimers();
        scheduleReconnect();
      };
    };

    // Fallback polling — only runs when the WS isn't connected.
    pollTimer.current = setInterval(() => {
      if (!connectedRef.current) {
        try { onSync?.(); } catch { /* caller unmounting */ }
      }
    }, pollMs);

    connect();

    return () => {
      cancelledRef.current = true;
      clearTimers();
      if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
      try { wsRef.current?.close?.(); } catch { /* already closed */ }
      wsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, wsUrl]);

  // Expose a way for callers to send frames (typing indicator, etc.).
  const send = (frame) => {
    const ws = wsRef.current;
    if (ws?.readyState !== 1) return false;
    try { ws.send(JSON.stringify(frame)); return true; } catch { return false; }
  };

  return { send };
}
